import React, { useEffect, useState } from "react";
import { todayISO } from "../lib/dates";
import { toast } from "sonner";
import { Banknote } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

export function ContactSettings() {
  const { data, reload } = useApi("/settings/contact");
  const [c, setC] = useState(null);
  useEffect(() => { if (data) setC(data); }, [data]);
  if (!c) return null;
  const save = async () => { try { await api.put("/admin/settings/contact", c); toast.success("Contact details updated on the public site"); reload(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };
  return (
    <div className="glass rounded-2xl p-5 space-y-3" data-testid="contact-settings">
      <h3 className="font-serif-display text-xl font-bold text-crimson">Public contact details</h3>
      <p className="text-xs text-muted-foreground">Shown in the site footer and on the Contact Us page.</p>
      <div><Label>Address</Label><Input value={c.address} onChange={(e) => setC({ ...c, address: e.target.value })} data-testid="contact-set-address" /></div>
      <div><Label>Email</Label><Input value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} data-testid="contact-set-email" /></div>
      <div><Label>Phone</Label><Input value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} data-testid="contact-set-phone" /></div>
      <div><Label>Meeting note (optional)</Label><Input value={c.meeting_note} onChange={(e) => setC({ ...c, meeting_note: e.target.value })} placeholder="e.g. General meetings: 2nd Sunday monthly, 3pm" data-testid="contact-set-note" /></div>
      <Button onClick={save} className="w-full bg-crimson" data-testid="contact-set-save">Save contact details</Button>
    </div>
  );
}

export function RecordPayout({ claim, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ amount: claim.amount_approved || claim.amount_requested || "", method: "check", reference: "", paid_on: todayISO(), note: "" });
  const submit = async () => {
    try { await api.post(`/admin/welfare/claims/${claim.id}/payout`, { ...f, amount: Number(f.amount) }); toast.success(`Payout of $${Number(f.amount).toFixed(2)} recorded`); setOpen(false); onDone(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="gap-1 border-gold text-crimson" data-testid={`wf-payout-${claim.id}`}><Banknote size={14} /> Record payment</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="payout-dialog">
          <DialogHeader><DialogTitle>Record welfare payment</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Paid to <strong>{claim.member_name || claim.member_id}</strong> for the {claim.type} claim.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount (USD)</Label><Input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="payout-amount" /></div>
            <div><Label>Method</Label><select value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="payout-method">{["check", "zelle", "cashapp", "cash", "bank transfer"].map((m) => <option key={m}>{m}</option>)}</select></div>
            <div><Label>Date paid</Label><Input type="date" value={f.paid_on} onChange={(e) => setF({ ...f, paid_on: e.target.value })} data-testid="payout-date" /></div>
            <div><Label>Reference / check #</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="payout-ref" /></div>
          </div>
          <div><Label>Note</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="payout-note" /></div>
          <DialogFooter><Button onClick={submit} disabled={!f.amount} className="bg-crimson" data-testid="payout-submit">Record payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function PayoutList({ claim }) {
  if (!claim.payouts?.length) return null;
  return (
    <ul className="mt-2 space-y-1 text-xs" data-testid={`payouts-${claim.id}`}>
      {claim.payouts.map((p) => <li key={p.id} className="flex justify-between rounded bg-palm/10 px-2 py-1"><span>${Number(p.amount).toFixed(2)} via {p.method}{p.reference ? ` · ${p.reference}` : ""}</span><span className="text-muted-foreground">{p.paid_on} · {p.recorded_by}</span></li>)}
    </ul>
  );
}
